
import { useState, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUserCredits } from "@/hooks/useUserCredits";

export interface PromptLabMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

export const usePromptLabSession = (promptId: string | null, modelId: string, userId: string | null) => {
  const [messages, setMessages] = useState<PromptLabMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [promptContent, setPromptContent] = useState<string | null>(null);
  const [promptTitle, setPromptTitle] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  
  // Use the credits hook directly
  const { credits, useCredit } = useUserCredits(userId);

  // Load prompt when promptId changes
  useEffect(() => {
    if (promptId) {
      loadPromptContent(promptId);
    } else {
      setPromptContent(null);
      setPromptTitle(null);
      
      // For free-form chat, add welcome message if no messages
      if (messages.length === 0) {
        const welcomeMessage: PromptLabMessage = {
          id: uuidv4(),
          role: "system",
          content: "I'm here to help. What would you like to chat about?",
          timestamp: new Date().toISOString()
        };
        setMessages([welcomeMessage]);
      }
    }
  }, [promptId]);

  // Check if user is admin
  useEffect(() => {
    if (userId) {
      checkAdminStatus();
    }
  }, [userId]);

  const checkAdminStatus = async () => {
    try {
      const { data: isAdmin, error } = await supabase.rpc("is_admin");
      
      if (error) {
        console.error("Error checking admin status:", error);
        return;
      }
      
      setIsUserAdmin(!!isAdmin);
    } catch (error) {
      console.error("Error checking admin status:", error);
    }
  };

  const loadPromptContent = async (id: string) => {
    setIsLoadingPrompt(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('title, content')
        .eq('id', id)
        .single();

      if (error) throw error;

      setPromptContent(data.content);
      setPromptTitle(data.title);

      // Reset messages when starting with a new prompt
      setMessages([]);
      
      // Create a welcome message from the assistant
      const welcomeMessage: PromptLabMessage = {
        id: uuidv4(),
        role: "system",
        content: `I'll help you test the "${data.title}" prompt. What would you like to try?`,
        timestamp: new Date().toISOString()
      };

      setMessages([welcomeMessage]);
    } catch (error: any) {
      console.error('Error loading prompt:', error);
      setError(error);
      toast.error("Failed to load prompt content");
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const createNewSession = async (): Promise<string> => {
    if (!userId) throw new Error("User must be logged in to create a session");

    try {
      // Create a new session
      const { data, error } = await supabase
        .from('product_chat_sessions')
        .insert({
          user_id: userId,
          title: promptTitle || "Free-form Chat Session",
          template_id: promptId
        })
        .select()
        .single();

      if (error) throw error;

      const newSessionId = data.id;
      setSessionId(newSessionId);

      return newSessionId;
    } catch (error) {
      console.error("Error creating new session:", error);
      throw error;
    }
  };

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;
    
    setError(null);
    
    if (!userId) {
      const error = new Error("Please log in to use this feature");
      setError(error);
      toast.error(error.message);
      return;
    }
    
    // Check if user has credits or is admin
    if (credits.remaining <= 0 && !isUserAdmin) {
      const error = new Error("You've used all your credits for this month");
      setError(error);
      toast.error(error.message);
      return;
    }

    // Prepare user message
    const userMessage: PromptLabMessage = {
      id: uuidv4(),
      role: "user",
      content: message,
      timestamp: new Date().toISOString()
    };

    // Optimistically update UI with user message
    setMessages((prev) => [...prev, userMessage]);
    
    // Show loading state
    setIsLoading(true);

    try {
      let currentSessionId = sessionId;

      // If no session exists, create one
      if (!currentSessionId) {
        currentSessionId = await createNewSession();
      }

      // Save user message to database
      const { error: messageError } = await supabase
        .from('product_chat_messages')
        .insert({
          session_id: currentSessionId,
          role: "user",
          content: message
        });

      if (messageError) throw messageError;

      // Use a credit (only for non-admin users)
      if (!isUserAdmin) {
        const creditUsed = await useCredit();
        
        if (!creditUsed) {
          throw new Error("You've used all your credits for this month");
        }
      }

      // Call OpenAI API via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke("openai-chat", {
        body: {
          userMessage: message,
          promptTemplate: promptContent,
          model: modelId,
          history: messages
            .filter(m => m.role !== "system")
            .map(m => ({
              role: m.role,
              content: m.content
            }))
        },
      });

      if (error) {
        console.error("Error from edge function:", error);
        throw new Error(error.message || "Failed to get a response from the AI");
      }

      if (!data || !data.message) {
        throw new Error("Invalid response from AI assistant");
      }

      // Prepare AI response message
      const aiMessage: PromptLabMessage = {
        id: uuidv4(),
        role: "assistant",
        content: data.message,
        timestamp: new Date().toISOString()
      };

      // Add AI message to UI
      setMessages((prev) => [...prev, aiMessage]);

      // Save AI message to database
      await supabase
        .from('product_chat_messages')
        .insert({
          session_id: currentSessionId,
          role: "assistant",
          content: data.message
        });

    } catch (error: any) {
      console.error("Error in chat:", error);
      
      // Add error message to the chat
      const errorMessage: PromptLabMessage = {
        id: uuidv4(),
        role: "assistant",
        content: `Sorry, there was an error: ${error.message || "Unknown error"}. Please try again.`,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setError(error);
      
      toast.error(error.message || "Failed to get a response. Please try again.");
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    isLoading,
    isLoadingPrompt,
    promptContent,
    promptTitle,
    sendMessage,
    remainingCredits: credits.remaining,
    isAdmin: isUserAdmin,
    error
  };
};
